import { ViewContainerRef } from "@angular/core";
import moment from "moment";
import { CheckInsRequestParam } from "src/app/utils/interfaces/new/check-ins-request-param";
import { IdentityToCheck } from "src/app/utils/interfaces/new/identity-to-check";
import { InsIdentityInfos } from "src/app/utils/interfaces/new/ins-identity-infos.interface";
import { LectureIns } from "src/app/utils/interfaces/new/lecture-ins.interface";

/**
 * Contrat commun des stratégies INSi :
 * - InsWebSocketService : connecteur DmpConnect-JS local (historique) ;
 * - InsRestService : API REST du proxy Espace de Confiance.
 * Sert de jeton d'injection : la factory (ins-service.factory.ts) choisit la stratégie à l'exécution.
 * Les méthodes publiques implémentées ici sont indépendantes de la stratégie : la factory les appelle de manière synchrone.
 */
export abstract class InsServiceAbstract {

  abstract resourceId: string;

  abstract getInsFromIdentityInformation(view: ViewContainerRef, identityInfos: InsIdentityInfos): Promise<LectureIns>;

  /**
   * La carte Vitale doit avoir été lue au préalable (CpxHandleServiceAbstract.lectureVitale).
   * vitaleIndex est la position du bénéficiaire dans le tableau Patients de cette lecture.
   */
  abstract getInsFromVitaleCard(view: ViewContainerRef, vitaleIndex: number): Promise<LectureIns>;

  abstract checkInsIdentity(view: ViewContainerRef, param: CheckInsRequestParam): Promise<boolean>;

  /** Vérification INSi en masse : le composant appelant est désactivé, non disponible avec l'API REST */
  abstract massCheckInsIdentity(view: ViewContainerRef, param: Array<IdentityToCheck>): Promise<any>;

  /**
   * Exigence SC.MSS/UX.32: dans le cas d'une identité non qualifiée, non récupérée, avec un NIR rentré manuellement
   * il faut "vérifier la clé de contrôle du numéro utilisé en guise de matricule INS",
   * donc. on effectue un "Modulo 97" (Clé calculée = 97 - (NIR % 97)), et on compare ensuite la clé calculée avec les 2 derniers chiffres du matricule INS,
   * si les 2 chiffres sont identique, le numéro est cohérent, sinon il est faux.
   *
   * Le numéro de NIR, doit aussi être cohérent avec le sexe, et la date de naissance du patient.
   */
  public validateNirAndCrossCheck(nir: string, nirKey: string, patientSexe?: string, patientDateNaissance?: string): boolean {
    if (!nir || nir.length !== 13 || !nirKey || nirKey.length !== 2) return false;

    let nirCalculated = nir;
    let offset = 0;

    // Règle spécifique à la Corse
    if (nir.includes('2A')) {
      nirCalculated = nir.replace('2A', '0');
      offset = 1000000;
    } else if (nir.includes('2B')) {
      nirCalculated = nir.replace('2B', '0');
      offset = 2000000;
    }

    if (Number.isNaN(Number(nirCalculated))) return false;

    // 1. Contrôle mathématique de la clé (Modulo 97)
    const calcResult = 97 - ((Number(nirCalculated) - offset) % 97);
    if (calcResult !== Number(nirKey)) return false;

    // 2. Contrôles de cohérence croisée
    const sexeNir = nir.substring(0, 1);
    const anneeNir = nir.substring(1, 3);
    const moisNir = nir.substring(3, 5);

    // Vérification du sexe
    if (patientSexe) {
      if (sexeNir === '1' && patientSexe !== 'HOMME') return false;
      if (sexeNir === '2' && patientSexe !== 'FEMME') return false;
    }

    // Vérification de la date de naissance
    if (patientDateNaissance) {
      const birthDate = moment(patientDateNaissance, ['YYYY-MM-DD', 'DD/MM/YYYY']);
      if (birthDate.isValid()) {
        const anneePatient = birthDate.format('YY');
        const moisPatient = birthDate.format('MM');

        if (anneeNir !== anneePatient) return false;

        // Tolérance pour les mois spéciaux (pseudo-NIR)
        if (moisNir !== moisPatient && Number(moisNir) <= 12) {
          return false;
        }
      }
    }

    return true;
  }

  /**
   * Vérifie que l'identité retournée par l'INSi est exploitable.
   * Les résultats 3 (plusieurs identités) et 1 (aucune identité) ne contiennent pas d'identité,
   * mais sont des retours métier valides qui doivent être remontés pour déclencher les exceptions associées.
   */
  protected isLectureInsOk(ins: LectureIns): boolean {
    if (ins.i_insIdentityResult === 3 || ins.i_insIdentityResult === 1) {
      return true;
    }

    if (ins.Identity) {
      // Si le mois ou le jour de la date de naissance est égale à '00' alors on retourne false
      if (ins.Identity.s_birthDate) {
        const split = ins.Identity.s_birthDate.split('-');
        if (split[1] === '00' || split[2] === '00') {
          return false;
        }
      } else {
        return false;
      }

      // Si le nom de naissance est vide alors on retourne false
      if (ins.Identity.s_birthName === '') {
        return false;
      }

      // Si le prénom de naissance est vide alors on retourne false
      if (ins.Identity.s_birthGiven === '') {
        return false;
      }

      // Si le sexe n'est ni 1, ni 2, ni 3 alors on retourne false
      if (ins.Identity.i_sex !== 1 && ins.Identity.i_sex !== 2 && ins.Identity.i_sex !== 3) {
        return false;
      }

      return true;
    } else {
      return false;
    }
  }
}
